import { Type } from '@angular/core';
import { MenuHeaderTitleImageComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-title/menu-header-title-image/menu-header-title-image.component';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { Section } from '../section/section';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import { SortVariantUtils } from '../../../utils/sort-variant-utils';
import { StringUtils } from '../../../utils/string-utils';
import { ColumnGrouping } from '../section/column-grouping';
import { ColumnUtils } from '../../../utils/column-utils';
import { PrintFooterLayoutType } from '../../enum/shared/print-footer-layout-type.enum';
import { ProductMenuFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/product-menu-footer.component';
import { PrintMenu } from '../print-menu';
import { exists } from '../../../functions/exists';
import { EmptyMenuFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/empty-menu-footer/empty-menu-footer.component';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { SectionColumnViewModel } from 'src/app/modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { WithinNameCell } from '../../enum/shared/within-name-cell';

/**
 * Viewport for fika print menu is 643 x 794. Makes the viewport 17cm by 21cm (6.69in by 8.27in) on an 8.5x11in page.
 */
export class FikaPrintMenu extends PrintMenu {
  getPageTopMargin(): string {
    return '1.37in';
  }

  getPageBottomMargin(): string {
    return '1.37in';
  }

  getPageLeftMargin(): string {
    return '0.9in';
  }

  getPageRightMargin(): string {
    return '0.9in';
  }

  getTitleSectionTopMargin(): string {
    return '0';
  }

  getTitleSectionBottomMargin(): string {
    return '0';
  }

  getThemeClass(): string {
    return 'fika-print-theme';
  }

  getHeaderType(): Type<MenuHeaderTitleImageComponent> {
    return MenuHeaderTitleImageComponent;
  }

  getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.None;
  }

  getShowInlineLabels(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  getShowProductSubtitle(): boolean {
    return false;
  }

  override fixedLocationBrandPropertyIsWithin(): WithinNameCell | null {
    return WithinNameCell.TitleLine;
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.ProductTitle, 1],
      [SectionColumnProductInfoType.Badge, 2],
      [SectionColumnProductInfoType.Strain, 3],
      [SectionColumnProductInfoType.StrainType, 4],
      [SectionColumnProductInfoType.Brand, 5],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(6),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(7),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(8),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(9),
      [SectionColumnProductInfoType.QuantityAndSize, 10],
      [SectionColumnProductInfoType.Quantity, 11],
      [SectionColumnProductInfoType.Size, 12],
      [SectionColumnProductInfoType.VariantType, 13],
      [SectionColumnProductInfoType.Stock, 14],
      [SectionColumnProductInfoType.GradingResults, 15],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 16],
      [SectionColumnProductInfoType.VariantPrice, 17]
    ]);
  }

  getShouldInflateColumnHeaderForRow(s: Section, col: SectionColumnViewModel, rows: SectionRowViewModel[]): boolean {
    const classification = col?.columnType === SectionColumnProductInfoType.StrainType;
    return !classification || (classification && exists(col?.columnTitle));
  }

  getShouldInflateColumnForRow(s: Section, col: SectionColumnViewModel, row: SectionRowViewModel): boolean {
    const badge = col?.columnType === SectionColumnProductInfoType.Badge;
    return !badge || (badge && exists(row?.hasBadges()));
  }

  getUniqueClassificationList(): StrainType[] {
    return super
      .getUniqueClassificationList()
      ?.filter(s => s !== StrainType.SativaDominant && s !== StrainType.IndicaDominant)
      ?.sort(SortVariantUtils.genericStrainTypeSort);
  }

  getPriceColumnTitle(col: SectionColumnViewModel): string {
    return StringUtils.removeWhiteSpace(super.getPriceColumnTitle(col));
  }

  getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getGridModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getSectionHeaderUnderlined(): boolean {
    return false;
  }

  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [FONT.PtMono.Bold_700, FONT.Poppins.SemiBold_600, FONT.Poppins.Medium_500, FONT.Poppins.Bold_700];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/wxq6frp.css';
  }

  getPrintFooterLayoutType(): PrintFooterLayoutType {
    return this.menuOptions?.printFooterLayout || PrintFooterLayoutType.None;
  }

  getFooterType(): Type<ProductMenuFooterComponent> {
    return EmptyMenuFooterComponent;
  }
}
